import React from 'react';
import { Alert, Button, Chip, Tooltip } from '@mui/material';
import { MdLocalShipping, MdCheckCircle, MdTimer, MdSchedule, MdWarningAmber, MdCurrencyRupee, MdAssignmentReturn, MdPercent } from 'react-icons/md';
import TabbedReport, { pill, TwoLine, Dash, pctChip } from './TabbedReport';
import { inr, fmtDate } from './reportShared';

const STATUS = {
  pending: ['#fef3c7', '#92400e'],
  'manifest-generated': ['#e0e7ff', '#3730a3'],
  'waiting-for-pickup': ['#fef3c7', '#92400e'],
  'pickup-requested': ['#e0f2fe', '#075985'],
  'picked-up': ['#cffafe', '#155e75'],
  'in-transit': ['#ede9fe', '#5b21b6'],
  'out-for-delivery': ['#fce7f3', '#9d174d'],
  delivered: ['#dcfce7', '#166534'],
};
const label = (s) => (s ? s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '-');
const statusChip = (s) => {
  const [bg, fg] = STATUS[s] || ['#f1f5f9', '#475569'];
  return <Chip size="small" label={label(s)} sx={pill(bg, fg)} />;
};
const money = (key, lbl, bold) => ({ key, label: lbl, numeric: true, bold, text: (r) => r[key], cell: (r) => inr(r[key]), print: (r) => inr(r[key]) });
const days = (v) => (v === null || v === undefined ? <Dash /> : `${v} d`);

const groupColumns = (nameLabel) => [
  { key: 'name', label: nameLabel, bold: true, text: (r) => r.name },
  { key: 'shipments', label: 'Shipments', numeric: true, text: (r) => r.shipments },
  { key: 'delivered', label: 'Delivered', numeric: true, text: (r) => r.delivered },
  { key: 'inTransit', label: 'In Transit', numeric: true, text: (r) => r.inTransit },
  { key: 'delayed', label: 'Delayed', numeric: true, text: (r) => r.delayed, cell: (r) => (r.delayed ? <Chip size="small" label={r.delayed} sx={pill('#fee2e2', '#b91c1c')} /> : 0) },
  { key: 'avgTatDays', label: 'Avg Delivery Time', center: true, text: (r) => r.avgTatDays ?? '', cell: (r) => days(r.avgTatDays) },
  { key: 'onTimePct', label: 'On-time %', center: true, text: (r) => (r.onTimePct === null ? '' : `${r.onTimePct}%`), cell: (r) => pctChip(r.onTimePct, { good: 90, warn: 75 }) },
  { key: 'avgPickupHours', label: 'Avg Pickup Time', center: true, text: (r) => r.avgPickupHours ?? '', cell: (r) => (r.avgPickupHours === null ? <Dash /> : `${r.avgPickupHours} h`) },
  { key: 'returnPct', label: 'Returned %', center: true, text: (r) => `${r.returnPct}%`, cell: (r) => pctChip(r.returnPct, { good: 2, warn: 5, invert: true }) },
  money('cost', 'Shipping Cost', true),
  money('avgCost', 'Avg Cost'),
  money('charged', 'Delivery Fees Charged'),
];

const shipmentCols = [
  { key: 'awb', label: 'AWB / Tracking', bold: true, text: (r) => r.awb },
  { key: 'orderNumber', label: 'Order No.', text: (r) => r.orderNumber },
  { key: 'courier', label: 'Courier', text: (r) => r.courier, cell: (r) => <TwoLine top={r.courier} bottom={r.reallocations ? `Reassigned ${r.reallocations}×` : ''} />, extra: (r) => ({ Reallocations: r.reallocations }) },
  { key: 'dest', label: 'Destination', text: (r) => [r.city, r.state].filter(Boolean).join(', ') },
  { key: 'shippedAt', label: 'Shipped', text: (r) => fmtDate(r.shippedAt) },
  { key: 'status', label: 'Status', text: (r) => label(r.status), cell: (r) => statusChip(r.status) },
  { key: 'expected', label: 'Expected', text: (r) => (r.expected ? fmtDate(r.expected) : ''), cell: (r) => (r.expected ? fmtDate(r.expected) : <Dash />) },
  {
    key: 'deliveredAt',
    label: 'Delivered',
    text: (r) => (r.deliveredAt ? fmtDate(r.deliveredAt) : ''),
    cell: (r) =>
      r.deliveredAt ? (
        <Tooltip title={r.onTime === null ? '' : r.onTime ? 'On time' : 'Late'} arrow>
          <span style={{ color: r.onTime === false ? '#b91c1c' : '#166534', fontWeight: 700 }}>{fmtDate(r.deliveredAt)}</span>
        </Tooltip>
      ) : r.delayed ? (
        <Chip size="small" icon={<MdWarningAmber />} label="Delayed" sx={{ ...pill('#fee2e2', '#b91c1c'), '& .MuiChip-icon': { color: 'inherit' } }} />
      ) : (
        <Dash />
      ),
  },
  { key: 'tatDays', label: 'Delivery Time', center: true, text: (r) => r.tatDays ?? '', cell: (r) => days(r.tatDays) },
  money('cost', 'Cost'),
  money('charged', 'Charged'),
];

const tabs = [
  { key: 'shipments', label: 'Shipments', defaultSort: 'date_desc', sorts: [['date_desc', 'Newest first'], ['tat_desc', 'Slowest delivery first'], ['cost_desc', 'Cost: high to low']], rowAlert: (r) => r.delayed, columns: shipmentCols },
  { key: 'couriers', label: 'By Courier', columns: groupColumns('Courier') },
  { key: 'states', label: 'By State', columns: groupColumns('State') },
  {
    key: 'stuck',
    label: 'Stuck Shipments',
    emptyText: 'No shipments are stuck — everything has moved in the last 3 days.',
    rowAlert: () => true,
    columns: [
      ...shipmentCols.slice(0, 6),
      { key: 'daysIdle', label: 'No Update For', center: true, text: (r) => r.daysIdle, cell: (r) => <Chip size="small" label={`${r.daysIdle} days`} sx={pill('#fee2e2', '#b91c1c')} /> },
      shipmentCols[6],
    ],
  },
];

const ShippingReport = () => (
  <TabbedReport
    route="reports/shipping-report"
    title="Shipping Report"
    subtitle="Courier performance, delivery times, delays and what shipping is costing you"
    filePrefix="Shipping"
    tabs={tabs}
    searchLabel="Search AWB / order no. / city / state"
    filters={[
      { key: 'courier', label: 'Courier', options: (data) => (data?.filterOptions?.couriers || []).map((c) => ({ value: c, label: c })) },
      { key: 'sstatus', label: 'Status', tabs: ['shipments'], options: [{ value: 'open', label: 'All in transit' }, { value: 'delayed', label: 'Delayed' }, ...Object.keys(STATUS).map((s) => ({ value: s, label: label(s) }))] },
    ]}
    cards={(s, { goTo, isActive }) => [
      { label: 'Shipments', value: (s.shipments || 0).toLocaleString('en-IN'), icon: MdLocalShipping, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: `${s.inTransit || 0} in transit`, onClick: () => goTo('shipments'), active: isActive('shipments') },
      { label: 'Delivered', value: (s.delivered || 0).toLocaleString('en-IN'), icon: MdCheckCircle, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: `${s.returned || 0} returned`, onClick: () => goTo('shipments', { sstatus: 'delivered' }), active: isActive('shipments', { sstatus: 'delivered' }) },
      { label: 'Avg Delivery Time', value: s.avgTatDays === null || s.avgTatDays === undefined ? '—' : `${s.avgTatDays} days`, icon: MdTimer, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: 'Order placed → delivered', onClick: () => goTo('couriers'), active: isActive('couriers') },
      { label: 'On-time Delivery', value: s.onTimePct === null || s.onTimePct === undefined ? '—' : `${s.onTimePct}%`, icon: MdSchedule, gradient: 'linear-gradient(135deg,#14b8a6,#0d9488)', sub: 'By the promised date' },
      { label: 'Delayed', value: (s.delayed || 0).toLocaleString('en-IN'), icon: MdWarningAmber, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: 'Past expected date, not delivered', onClick: () => goTo('shipments', { sstatus: 'delayed' }), active: isActive('shipments', { sstatus: 'delayed' }) },
      { label: 'Stuck', value: (s.stuck || 0).toLocaleString('en-IN'), icon: MdAssignmentReturn, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: 'No update for 3+ days', onClick: () => goTo('stuck'), active: isActive('stuck') },
      { label: 'Shipping Cost', value: inr(s.cost), icon: MdCurrencyRupee, gradient: 'linear-gradient(135deg,#a855f7,#7e22ce)', sub: `Avg ${inr(s.avgCost)} per shipment` },
      { label: 'Cost Recovered', value: s.recoveryPct === null || s.recoveryPct === undefined ? '—' : `${s.recoveryPct}%`, icon: MdPercent, gradient: 'linear-gradient(135deg,#0f172a,#334155)', sub: `${inr(s.charged)} delivery fees charged` },
    ]}
    banner={(s, { goTo }) =>
      s.stuck > 0 ? (
        <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => goTo('stuck')}>Show</Button>}>
          {s.stuck} shipment(s) haven't had a status update in 3+ days — follow up with the courier before customers chase you.
        </Alert>
      ) : null
    }
  />
);

export default ShippingReport;
